import React, { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Formik, FormikHelpers } from 'formik'
import { Input, Form } from 'antd'
import * as Yup from 'yup'
import { useMutation, useQuery } from '@apollo/react-hooks'
import { AkaButton, AkaTypography } from 'components'
import { getPasswordSchema } from '../../../../utils/validations/validationSchema'
import { REGISTRATION_REQUEST } from '../../../core/mutations'
import { REGISTRATION_REQUEST_CAPTCHA } from '../../../core/queries'
import { cleanUpGraphQLErrorMessage } from 'utils/helper.utils'
import { RegistrationFormValues } from './types'

const FormItem = Form.Item
const INVALID_CAPTCHA_ERROR_CODE = 'INVALID_CAPTCHA'

export const RegistrationRequest: React.FC = () => {
	const [t] = useTranslation()
	const {
		data: captchaData,
		loading: captchaLoading,
		refetch: refetchCaptcha
	} = useQuery(REGISTRATION_REQUEST_CAPTCHA)
	const captchaChallenge = captchaData?.captchaChallenge
	const refreshCaptcha = useCallback(() => {
		if (!refetchCaptcha) {
			return
		}

		setCaptchaError('')
		refetchCaptcha().catch(() => {})
	}, [refetchCaptcha])
	const resetFormRef = useRef<() => void>(() => {})
	const [formStatus, setFormStatus] = useState<'form' | 'success' | 'error'>(
		'form'
	)
	const [errorMessage, setErrorMessage] = useState<string>('')
	const [captchaError, setCaptchaError] = useState<string>('')

	const validationSchema = Yup.object().shape({
		firstName: Yup.string().required(
			t('auth.registrationRequestPage.firstNameRequired')
		),
		lastName: Yup.string().required(
			t('auth.registrationRequestPage.lastNameRequired')
		),
		email: Yup.string()
			.email(t('auth.validations.email'))
			.required(t('auth.emailRequired')),
		password: getPasswordSchema(),
		confirmPassword: Yup.string()
			.oneOf(
				[Yup.ref('password'), null],
				t('account.changePasswordForm.validation.notMatchingPasswords')
			)
			.required(t('account.changePasswordForm.validation.confirmNewPassword')),
		captchaValue: Yup.string().required(
			t('auth.registrationRequestPage.captchaRequired')
		)
	})

	const [registerUser, { loading }] = useMutation(REGISTRATION_REQUEST, {
		onCompleted: () => {
			refreshCaptcha()
			setFormStatus('success')
			if (resetFormRef.current) resetFormRef.current()
		},
		onError: error => {
			refreshCaptcha()
			const message = cleanUpGraphQLErrorMessage(error.message)
			const graphQLErrorCode = error?.graphQLErrors?.find(
				err => err?.extensions?.code
			)?.extensions?.code
			type ErrorWithResponseCode = {
				code?: string
				response?: {
					data?: {
						code?: string
					}
				}
			}
			const errorWithResponse = error as ErrorWithResponseCode
			const errorCode =
				errorWithResponse.code ??
				errorWithResponse.response?.data?.code ??
				graphQLErrorCode

			const isInvalidCaptchaCode = errorCode === INVALID_CAPTCHA_ERROR_CODE

			if (isInvalidCaptchaCode) {
				setCaptchaError(t('auth.registrationRequestPage.captchaInvalid'))
				setErrorMessage('')
				setFormStatus('form')
				return
			}

			setCaptchaError('')
			const userMessage = t('auth.registrationRequestPage.errorMessage', {
				message
			})
			setErrorMessage(userMessage)
			setFormStatus('error')
		}
	})

	const handleSubmit = async (
		values: RegistrationFormValues,
		{ resetForm }: FormikHelpers<RegistrationFormValues>
	) => {
		resetFormRef.current = resetForm
		setCaptchaError('')
		if (!captchaChallenge?.hashKey) {
			const userMessage = t('auth.registrationRequestPage.captchaNotLoaded')
			setErrorMessage(userMessage)
			setFormStatus('error')
			return
		}

		await registerUser({
			variables: {
				...values,
				captchaKey: captchaChallenge.hashKey
			}
		})
	}

	return (
		<>
			{formStatus === 'success' ? (
				<>
					<AkaTypography
						variant="h1"
						style={{ fontWeight: 'bold', margin: '32px auto' }}
					>
						{t('auth.registrationRequestPage.thankYou')}
					</AkaTypography>
					<AkaTypography variant="bodySegoe">
						{t('auth.registrationRequestPage.successMessage')
							.split('\n')
							.map((paragraph, index) => (
								<p key={index} style={{ width: '100%', marginBottom: '10px' }}>
									{paragraph}
								</p>
							))}
					</AkaTypography>
				</>
			) : formStatus === 'error' ? (
				<AkaTypography
					variant="bodySegoe"
					color="error"
					style={{ margin: '32px auto' }}
				>
					{errorMessage}
				</AkaTypography>
			) : (
				<>
					{errorMessage && (
						<AkaTypography
							variant="bodySegoe"
							color="error"
							style={{ margin: '32px auto' }}
						>
							{errorMessage}
						</AkaTypography>
					)}
					<Formik
						initialValues={{
							firstName: '',
							lastName: '',
							email: '',
							password: '',
							confirmPassword: '',
							captchaValue: ''
						}}
						validationSchema={validationSchema}
						onSubmit={handleSubmit}
					>
						{({
							values,
							handleChange,
							handleBlur,
							handleSubmit,
							errors,
							touched
						}) => (
							<>
								<AkaTypography
									variant="h1"
									style={{
										fontWeight: 'bold',
										margin: '32px auto'
									}}
								>
									{t('auth.registrationRequestPage.registration')}
								</AkaTypography>
								<AkaTypography variant="bodySegoe">
									{t('auth.registrationRequestPage.info')
										.split('\n')
										.map((paragraph, index) => (
											<p
												key={index}
												style={{ width: '100%', marginBottom: '10px' }}
											>
												{paragraph}
											</p>
										))}
								</AkaTypography>
								<Form
									style={{ fontFamily: 'SegoeUI, sans-serif' }}
									onFinish={handleSubmit}
								>
									<FormItem>
										<div
											style={{
												display: 'flex',
												flexDirection: 'column',
												gap: 8,
												width: 500
											}}
										>
											<FormItem
												validateStatus={
													touched.firstName && errors.firstName ? 'error' : ''
												}
												help={touched.firstName && errors.firstName}
												style={{ marginBottom: 8, maxWidth: 500 }}
											>
												<Input
													name="firstName"
													value={values.firstName}
													onChange={handleChange}
													onBlur={handleBlur}
													placeholder={t(
														'auth.registrationRequestPage.firstName'
													)}
													style={{ maxWidth: 500 }}
												/>
											</FormItem>
											<FormItem
												validateStatus={
													touched.lastName && errors.lastName ? 'error' : ''
												}
												help={touched.lastName && errors.lastName}
												style={{ marginBottom: 8, maxWidth: 500 }}
											>
												<Input
													name="lastName"
													value={values.lastName}
													onChange={handleChange}
													onBlur={handleBlur}
													placeholder={t(
														'auth.registrationRequestPage.lastName'
													)}
													style={{ maxWidth: 500 }}
												/>
											</FormItem>
											<FormItem
												validateStatus={
													touched.email && errors.email ? 'error' : ''
												}
												help={touched.email && errors.email}
												style={{ marginBottom: 8, maxWidth: 500 }}
											>
												<Input
													name="email"
													type="email"
													value={values.email}
													onChange={handleChange}
													onBlur={handleBlur}
													placeholder={t('auth.email')}
													style={{ maxWidth: 500 }}
												/>
											</FormItem>
											<FormItem
												validateStatus={
													touched.password && errors.password ? 'error' : ''
												}
												help={touched.password && errors.password}
												style={{ marginBottom: 8, maxWidth: 500 }}
											>
												<Input.Password
													name="password"
													value={values.password}
													onChange={handleChange}
													onBlur={handleBlur}
													placeholder={t('auth.password')}
													style={{ maxWidth: 500 }}
												/>
											</FormItem>
											<FormItem
												validateStatus={
													touched.confirmPassword && errors.confirmPassword
														? 'error'
														: ''
												}
												help={touched.confirmPassword && errors.confirmPassword}
												style={{ marginBottom: 8, maxWidth: 500 }}
											>
												<Input.Password
													name="confirmPassword"
													value={values.confirmPassword}
													onChange={handleChange}
													onBlur={handleBlur}
													placeholder={t(
														'auth.registrationRequestPage.confirmPassword'
													)}
													style={{ maxWidth: 500 }}
												/>
											</FormItem>
											<div
												style={{
													marginBottom: 8,
													maxWidth: 500
												}}
											>
												<AkaTypography
													variant="bodySegoe"
													style={{ fontWeight: '600' }}
												>
													{t('auth.registrationRequestPage.captchaLabel')}
												</AkaTypography>
												<div
													style={{
														display: 'flex',
														alignItems: 'center',
														gap: 12
													}}
												>
													{captchaLoading ? (
														<AkaTypography variant="bodySegoe">
															{t('auth.registrationRequestPage.captchaLoading')}
														</AkaTypography>
													) : captchaChallenge?.imageUrl ? (
														<img
															src={captchaChallenge.imageUrl}
															alt={t(
																'auth.registrationRequestPage.captchaImageAlt'
															)}
															style={{
																height: 80,
																borderRadius: 6,
																border: '1px solid #d9d9d9'
															}}
														/>
													) : (
														<AkaTypography variant="bodySegoe" color="error">
															{t(
																'auth.registrationRequestPage.captchaNotLoaded'
															)}
														</AkaTypography>
													)}
													<AkaButton
														type="link"
														onClick={refreshCaptcha}
														disabled={captchaLoading}
													>
														{t('auth.registrationRequestPage.captchaRefresh')}
													</AkaButton>
												</div>
											</div>
											<FormItem
												validateStatus={
													captchaError ||
													(touched.captchaValue && errors.captchaValue)
														? 'error'
														: ''
												}
												help={
													captchaError ||
													(touched.captchaValue && errors.captchaValue)
												}
												style={{ marginBottom: 8, maxWidth: 500 }}
											>
												<Input
													name="captchaValue"
													value={values.captchaValue}
													onChange={handleChange}
													onBlur={handleBlur}
													placeholder={t(
														'auth.registrationRequestPage.captchaPlaceholder'
													)}
													style={{ maxWidth: 500 }}
												/>
											</FormItem>
										</div>
									</FormItem>
									<FormItem>
										<AkaButton
											type="primary"
											htmlType="submit"
											loading={loading}
										>
											{t(
												'auth.registrationRequestPage.submitRegistrationRequest'
											)}
										</AkaButton>
									</FormItem>
								</Form>
							</>
						)}
					</Formik>
				</>
			)}
		</>
	)
}
